import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAdditionalUserInfo,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { auth, db, googleProvider } from './firebase-config';
import { ensureCompanyBootstrap } from './core/company/companyBootstrap';
import {
  resolveCompaniesCollectionPath,
  resolveCompanyDocPath,
  resolveCompanyGeneralSettingsPath,
  resolveCompanyMainSettingsPath,
  resolveCompanyModulesSettingsPath,
  resolveCompanyOfficeSettingsPath,
  resolveCompanyPlanSettingsPath,
} from './core/company/companyPaths';
import { buildCompanyAccessPath, sanitizeCompanyAccessSlug } from './core/company/companyAccess';

const TRIAL_DAYS = 30;
const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';

const initialForm = {
  companyName: '',
  responsibleName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  addressLine: '',
  locality: '',
  province: 'Buenos Aires',
  country: 'Argentina',
  postalCode: '',
  acceptGeolocation: false,
  acceptPrivacy: false,
};

const initialAddressValidation = {
  status: GEOAPIFY_API_KEY ? 'idle' : 'missing-key', // idle | loading | success | error | missing-key
  text: GEOAPIFY_API_KEY
    ? 'Escribí la dirección y elegí una sugerencia para validar la base operativa.'
    : 'Falta configurar REACT_APP_GEOAPIFY_API_KEY para activar las sugerencias de dirección.',
  coords: null,
  validatedAddress: '',
};

const trialModules = {
  stock: { enabled: false },
  logistics: { enabled: true },
  routeMonitor: { enabled: false },
  reports: { enabled: false },
  fleetExpenses: { enabled: false },
  systemGuide: { enabled: true },
};

const toInputValue = (value) => String(value || '').trim();

const composeFullAddress = (values = {}) => {
  return [
    toInputValue(values.addressLine),
    toInputValue(values.locality),
    toInputValue(values.province),
    toInputValue(values.country),
    toInputValue(values.postalCode),
  ]
    .filter(Boolean)
    .join(', ')
    .trim();
};

const isValidEmailFormat = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
};

const buildUniqueCompanyIdentity = async (companyName = '') => {
  const baseSlug = sanitizeCompanyAccessSlug(companyName, 'empresa');
  const companiesRef = collection(db, resolveCompaniesCollectionPath());

  let accessSlug = baseSlug;
  let suffix = 0;

  while (true) {
    const candidate = suffix === 0 ? baseSlug : `${baseSlug}-${suffix + 1}`;
    const snapshot = await getDocs(query(companiesRef, where('accessSlug', '==', candidate)));
    if (snapshot.empty) {
      accessSlug = candidate;
      break;
    }
    suffix += 1;
  }

  const companyId = `${accessSlug}-${Date.now().toString(36)}`;

  return {
    accessSlug,
    companyId,
  };
};

const resolveFriendlyError = (error) => {
  const code = String(error?.code || '').trim();

  switch (code) {
    case 'auth/email-already-in-use':
      return 'Ya existe una cuenta con este correo. Ingresá con ese acceso o usá otro email para crear el trial.';
    case 'auth/invalid-email':
      return 'El correo ingresado no es válido.';
    case 'auth/weak-password':
      return 'La contraseña es muy débil. Usá al menos 6 caracteres.';
    case 'auth/network-request-failed':
      return 'No hay conexión suficiente para completar el alta. Revisá internet e intentá otra vez.';
    case 'auth/google-session-missing':
      return 'No encontramos una sesión activa de Google para completar esta inscripción.';
    case 'auth/email-mismatch':
      return 'La cuenta Google conectada no coincide con el correo cargado en el formulario.';
    default:
      return 'No pudimos crear el espacio trial en este momento. Probá de nuevo en unos instantes.';
  }
};

const resolveFriendlyGoogleError = (error) => {
  const code = String(error?.code || '').trim();

  switch (code) {
    case 'auth/popup-closed-by-user':
      return 'Se cerró la ventana de Google antes de completar el acceso.';
    case 'auth/popup-blocked':
    case 'auth/cancelled-popup-request':
      return 'El navegador bloqueó la ventana de Google. Volvé a intentarlo o usá contraseña.';
    case 'auth/account-exists-with-different-credential':
      return 'Ese correo ya existe con otro método de acceso. Probá ingresar con la contraseña actual.';
    case 'auth/unauthorized-domain':
      return 'Este dominio todavía no está autorizado para Google en Firebase Authentication.';
    case 'auth/configuration-not-found':
      return 'Google todavía no quedó completamente configurado en Firebase Authentication.';
    case 'auth/operation-not-allowed':
      return 'Google todavía no está habilitado como proveedor de acceso.';
    default:
      return error?.message || 'No pudimos vincular Google en este momento.';
  }
};

const appendStepContext = (message = '', stepLabel = '') => {
  const baseMessage = String(message || '').trim();
  const normalizedStep = String(stepLabel || '').trim();
  if (!normalizedStep) return baseMessage;
  return `${baseMessage} Paso que falló: ${normalizedStep}.`;
};

const getCountryCode = (country = '') => {
  const normalized = String(country || '').trim().toLowerCase();

  const map = {
    argentina: 'ar',
    uruguay: 'uy',
    chile: 'cl',
    paraguay: 'py',
    bolivia: 'bo',
    brasil: 'br',
    brazil: 'br',
    peru: 'pe',
    perú: 'pe',
  };

  return map[normalized] || 'ar';
};

const TrialSignupPage = ({ onBackHome, onGoToLogin, onComplete }) => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [redirectTarget, setRedirectTarget] = useState('');
  const [createdCompanyName, setCreatedCompanyName] = useState('');
  const [authMode, setAuthMode] = useState('password');
  const [googleAccount, setGoogleAccount] = useState(null);
  const [connectingGoogle, setConnectingGoogle] = useState(false);

  const [addressValidation, setAddressValidation] = useState(initialAddressValidation);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const autocompleteWrapRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const fullAddress = useMemo(() => composeFullAddress(form), [form]);

  const canSubmit = useMemo(() => {
    const hasCredentialSource =
      authMode === 'google'
        ? Boolean(googleAccount?.uid && googleAccount?.email)
        : Boolean(form.password.trim() && form.confirmPassword.trim());

    return (
      Boolean(GEOAPIFY_API_KEY) &&
      form.companyName.trim() &&
      form.responsibleName.trim() &&
      form.email.trim() &&
      hasCredentialSource &&
      form.addressLine.trim() &&
      form.locality.trim() &&
      form.province.trim() &&
      form.country.trim() &&
      form.acceptGeolocation &&
      form.acceptPrivacy &&
      addressValidation.status === 'success' &&
      addressValidation.validatedAddress === fullAddress &&
      addressValidation.coords
    );
  }, [addressValidation, authMode, form, fullAddress, googleAccount]);

  const resetAddressValidation = (message) => {
    setAddressValidation({
      status: GEOAPIFY_API_KEY ? 'idle' : 'missing-key',
      text:
        message ||
        (GEOAPIFY_API_KEY
          ? 'La dirección cambió. Elegí una sugerencia para volver a validar la base operativa.'
          : 'Falta configurar REACT_APP_GEOAPIFY_API_KEY para activar las sugerencias de dirección.'),
      coords: null,
      validatedAddress: '',
    });
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (['addressLine', 'locality', 'province', 'country', 'postalCode'].includes(name)) {
        resetAddressValidation();
      }

      return next;
    });
  };

  const applySuggestion = (item) => {
    const nextForm = {
      ...form,
      addressLine: toInputValue(item.address_line1 || item.street || form.addressLine),
      locality: toInputValue(
        item.city || item.town || item.suburb || item.county || form.locality
      ),
      province: toInputValue(item.state || form.province),
      country: toInputValue(item.country || form.country || 'Argentina'),
      postalCode: toInputValue(item.postcode || ''),
    };

    const nextFullAddress = composeFullAddress(nextForm);

    setForm(nextForm);
    setSuggestions([]);
    setShowSuggestions(false);

    if (item.lat != null && item.lon != null) {
      setAddressValidation({
        status: 'success',
        text: 'Dirección seleccionada correctamente. La base operativa ya quedó validada.',
        coords: {
          lat: Number(item.lat),
          lng: Number(item.lon),
        },
        validatedAddress: nextFullAddress,
      });
      return;
    }

    setAddressValidation({
      status: 'error',
      text: 'No pudimos validar la dirección seleccionada. Probá con otra sugerencia.',
      coords: null,
      validatedAddress: '',
    });
  };

  useEffect(() => {
    if (!GEOAPIFY_API_KEY) return;

    const queryText = toInputValue(form.addressLine);
    const countryCode = getCountryCode(form.country);

    if (abortRef.current) {
      abortRef.current.abort();
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (queryText.length < 3) {
      setSuggestions([]);
      setLoadingSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    setAddressValidation((prev) =>
      prev.status === 'success'
        ? prev
        : {
            ...prev,
            status: 'loading',
            text: 'Buscando direcciones sugeridas...',
          }
    );

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const url =
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(queryText)}` +
          `&filter=countrycode:${countryCode}` +
          `&limit=5&lang=es&format=json&apiKey=${GEOAPIFY_API_KEY}`;

        const response = await fetch(url, { signal: controller.signal });

        if (!response.ok) {
          throw new Error(`Geoapify autocomplete error: ${response.status}`);
        }

        const data = await response.json();
        const results = Array.isArray(data?.results) ? data.results : [];

        setSuggestions(results);
        setShowSuggestions(true);
        setLoadingSuggestions(false);

        setAddressValidation((prev) =>
          prev.status === 'success'
            ? prev
            : {
                ...prev,
                status: results.length ? 'idle' : 'error',
                text: results.length
                  ? 'Elegí una dirección de la lista para validar la base operativa.'
                  : 'No encontramos coincidencias. Probá con más datos de calle y número.',
              }
        );
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('Error buscando sugerencias Geoapify:', error);
        setSuggestions([]);
        setShowSuggestions(false);
        setLoadingSuggestions(false);
        setAddressValidation({
          status: 'error',
          text: 'No pudimos cargar sugerencias de dirección en este momento.',
          coords: null,
          validatedAddress: '',
        });
      }
    }, 350);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [form.addressLine, form.country]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!autocompleteWrapRef.current?.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!redirectTarget) return undefined;

    const timeoutId = window.setTimeout(() => {
      if (typeof onComplete === 'function') {
        onComplete(redirectTarget);
        return;
      }

      if (typeof window !== 'undefined') {
        window.location.assign(redirectTarget);
      }
    }, 180);

    return () => window.clearTimeout(timeoutId);
  }, [onComplete, redirectTarget]);

  useEffect(() => {
    const activeUser = auth.currentUser;
    const hasGoogleProvider = Array.isArray(activeUser?.providerData)
      && activeUser.providerData.some((provider) => provider?.providerId === 'google.com');

    if (!activeUser?.uid || !activeUser.email || !hasGoogleProvider) return;

    setAuthMode('google');
    setGoogleAccount({
      uid: activeUser.uid,
      email: activeUser.email,
      displayName: activeUser.displayName || '',
      isNewUser: false,
    });
    setForm((prev) => ({
      ...prev,
      email: prev.email || activeUser.email || '',
      responsibleName: prev.responsibleName || activeUser.displayName || '',
      password: '',
      confirmPassword: '',
    }));
  }, []);

  const activateGoogleMode = async () => {
    if (connectingGoogle || submitting) return;

    setConnectingGoogle(true);
    setStatus({ type: '', text: '' });

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const additionalInfo = getAdditionalUserInfo(result);

      setAuthMode('google');
      setGoogleAccount({
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || '',
        isNewUser: Boolean(additionalInfo?.isNewUser),
      });
      setForm((prev) => ({
        ...prev,
        email: user.email || prev.email,
        responsibleName: prev.responsibleName || user.displayName || '',
        password: '',
        confirmPassword: '',
      }));
      setStatus({
        type: 'success',
        text: 'Google quedó vinculado para esta inscripción. Ahora completá los datos de la empresa y la base operativa.',
      });
    } catch (error) {
      console.error('Error vinculando Google al alta trial:', error);
      setStatus({
        type: 'error',
        text: resolveFriendlyGoogleError(error),
      });
    } finally {
      setConnectingGoogle(false);
    }
  };

  const switchToPasswordMode = async () => {
    if (submitting) return;

    try {
      if (auth.currentUser?.uid === googleAccount?.uid) {
        await signOut(auth);
      }
    } catch (error) {
      console.error('No se pudo cerrar la sesión temporal de Google:', error);
    }

    setAuthMode('password');
    setGoogleAccount(null);
    setForm((prev) => ({
      ...prev,
      password: '',
      confirmPassword: '',
    }));
    setStatus({
      type: '',
      text: '',
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;

    let createdSession = false;
    let createdUser = null;
    let savedBaseUserDoc = false;
    let currentStep = 'validar datos';
    let completedRedirect = false;
    let authProviderKind = authMode;

    if (!GEOAPIFY_API_KEY) {
      setStatus({
        type: 'error',
        text: 'Falta configurar la API key de Geoapify para validar la dirección operativa.',
      });
      return;
    }

    if (!isValidEmailFormat(form.email)) {
      setStatus({
        type: 'error',
        text: 'Ingresá un correo válido para crear el acceso de la empresa.',
      });
      return;
    }

    if (authMode === 'password' && form.password !== form.confirmPassword) {
      setStatus({
        type: 'error',
        text: 'Las contraseñas no coinciden. Revisalas y volvé a intentar.',
      });
      return;
    }

    if (authMode === 'password' && form.password.trim().length < 6) {
      setStatus({
        type: 'error',
        text: 'La contraseña debe tener al menos 6 caracteres.',
      });
      return;
    }

    if (authMode === 'google' && (!googleAccount?.uid || !googleAccount?.email)) {
      setStatus({
        type: 'error',
        text: 'Primero vinculá una cuenta de Google para completar el alta con ese método.',
      });
      return;
    }

    if (
      addressValidation.status !== 'success' ||
      addressValidation.validatedAddress !== fullAddress ||
      !addressValidation.coords
    ) {
      setStatus({
        type: 'error',
        text: 'Primero elegí una dirección sugerida para validar correctamente la base operativa.',
      });
      return;
    }

    setSubmitting(true);
    setStatus({ type: '', text: '' });
    setRedirectTarget('');
    setCreatedCompanyName('');

    try {
      currentStep = 'crear acceso de autenticación';
      const companyName = form.companyName.trim();
      const responsibleName = form.responsibleName.trim();
      const email = form.email.trim().toLowerCase();
      const phone = form.phone.trim();
      const validatedCoords = addressValidation.coords;
      let user = null;

      if (authMode === 'google') {
        currentStep = 'validar acceso autenticado con Google';
        user = auth.currentUser;

        if (!user?.uid || !user?.email) {
          throw Object.assign(new Error('No se encontró una sesión Google válida.'), {
            code: 'auth/google-session-missing',
          });
        }

        if (String(user.email || '').trim().toLowerCase() !== email) {
          throw Object.assign(
            new Error('La cuenta Google activa no coincide con el correo cargado en el formulario.'),
            {
              code: 'auth/email-mismatch',
            }
          );
        }

        createdSession = true;
        createdUser = user;
        await user.getIdToken(true);
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, form.password);
        user = userCredential.user;
        createdSession = true;
        createdUser = user;
        await user.getIdToken(true);
      }

      const { accessSlug, companyId } = await buildUniqueCompanyIdentity(companyName);
      const trialStartedAt = serverTimestamp();
      const trialEndsAt = Timestamp.fromDate(
        new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000)
      );
      const accessPath = buildCompanyAccessPath(accessSlug);
      const userRef = doc(db, 'users', user.uid);

      currentStep = 'guardar usuario base de la empresa';
      await setDoc(
        userRef,
        {
          uid: user.uid,
          email,
          role: 'companyAdmin',
          accessScope: 'company',
          companyId,
          companyIds: [companyId],
          companyName,
          accessSlug,
          companySlug: accessSlug,
          allowedCompanies: [{ id: companyId, name: companyName }],
          availableCompanies: [{ id: companyId, name: companyName }],
          isTrialOwner: true,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      savedBaseUserDoc = true;

      currentStep = 'crear estructura inicial de la empresa';
      await ensureCompanyBootstrap({
        companyId,
        companyName,
        status: 'trial',
        planId: 'trial',
        planName: 'Plan Trial',
        active: true,
        isSandbox: true,
        modules: trialModules,
        office: {
          officeName: companyName,
          name: companyName,
          locality: form.locality.trim(),
          province: form.province.trim(),
          country: form.country.trim(),
          postalCode: form.postalCode.trim(),
          fullAddress,
          address: fullAddress,
          lat: validatedCoords.lat,
          lng: validatedCoords.lng,
          geocodingStatus: 'validated',
        },
        main: {
          accessSlug,
          companySlug: accessSlug,
          trialDays: TRIAL_DAYS,
          trialStatus: 'active',
          trialEndsAt,
        },
      });

      const rootRef = doc(db, resolveCompanyDocPath(companyId));
      const mainRef = doc(db, resolveCompanyMainSettingsPath(companyId));
      const generalRef = doc(db, resolveCompanyGeneralSettingsPath(companyId));
      const officeRef = doc(db, resolveCompanyOfficeSettingsPath(companyId));
      const planRef = doc(db, resolveCompanyPlanSettingsPath(companyId));
      const modulesRef = doc(db, resolveCompanyModulesSettingsPath(companyId));

      currentStep = 'guardar configuración operativa del trial';
      await Promise.all([
        setDoc(
          rootRef,
          {
            companyId,
            companyName,
            responsibleName,
            contactEmail: email,
            contactPhone: phone,
            officeName: companyName,
            locality: form.locality.trim(),
            province: form.province.trim(),
            country: form.country.trim(),
            postalCode: form.postalCode.trim(),
            fullAddress,
            accessSlug,
            companySlug: accessSlug,
            active: true,
            isActive: true,
            status: 'trial',
            plan: 'Plan Trial',
            planId: 'trial',
            planName: 'Plan Trial',
            trialDays: TRIAL_DAYS,
            trialStatus: 'active',
            trialStartedAt,
            trialEndsAt,
            trialIncludes: ['logistics', 'systemGuide'],
            lat: validatedCoords.lat,
            lng: validatedCoords.lng,
            geocodingStatus: 'validated',
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ),
        setDoc(
          mainRef,
          {
            companyId,
            companyName,
            accessSlug,
            companySlug: accessSlug,
            trialDays: TRIAL_DAYS,
            trialStatus: 'active',
            trialStartedAt,
            trialEndsAt,
            officeLocation: {
              name: companyName,
              address: fullAddress,
              lat: validatedCoords.lat,
              lng: validatedCoords.lng,
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ),
        setDoc(
          generalRef,
          {
            companyId,
            companyName,
            responsibleName,
            contactEmail: email,
            contactPhone: phone,
            locality: form.locality.trim(),
            province: form.province.trim(),
            country: form.country.trim(),
            postalCode: form.postalCode.trim(),
            fullAddress,
            plan: 'Plan Trial',
            planId: 'trial',
            planName: 'Plan Trial',
            status: 'trial',
            trialDays: TRIAL_DAYS,
            trialStatus: 'active',
            trialStartedAt,
            trialEndsAt,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ),
        setDoc(
          officeRef,
          {
            officeName: companyName,
            name: companyName,
            locality: form.locality.trim(),
            province: form.province.trim(),
            country: form.country.trim(),
            postalCode: form.postalCode.trim(),
            fullAddress,
            address: fullAddress,
            lat: validatedCoords.lat,
            lng: validatedCoords.lng,
            geocodingStatus: 'validated',
            geocodedAt: serverTimestamp(),
            reference: '',
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ),
        setDoc(
          planRef,
          {
            planId: 'trial',
            planName: 'Plan Trial',
            label: 'Plan Trial',
            status: 'trial',
            trialDays: TRIAL_DAYS,
            trialStatus: 'active',
            trialStartedAt,
            trialEndsAt,
            includes: {
              logistics: true,
              routeCreation: true,
              transporters: true,
              contacts: true,
              routeSheets: true,
              liveMonitor: false,
              reports: false,
              fleetExpenses: false,
              stock: false,
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ),
        setDoc(
          modulesRef,
          {
            ...trialModules,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ),
      ]);

      localStorage.setItem('activeCompanyId', companyId);
      setStatus({
        type: 'success',
        text: 'Empresa creada correctamente. Estamos preparando tu espacio de trabajo.',
      });
      setCreatedCompanyName(companyName);
      setRedirectTarget(accessPath);
      completedRedirect = true;
    } catch (error) {
      console.error('Error creando empresa trial:', error);

      if (createdUser && !savedBaseUserDoc && authProviderKind === 'password') {
        try {
          await deleteUser(createdUser);
        } catch (deleteError) {
          console.error('No se pudo eliminar el usuario creado parcialmente:', deleteError);
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.error('No se pudo cerrar la sesión temporal del trial:', signOutError);
          }
        }
      } else if (createdSession) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error('No se pudo cerrar la sesión temporal del trial:', signOutError);
        }
      }

      setStatus({
        type: 'error',
        text: appendStepContext(resolveFriendlyError(error), currentStep),
      });
    } finally {
      if (!completedRedirect) {
        setSubmitting(false);
      }
    }
  };

  const addressStatusClasses = {
    idle: 'border-slate-200 bg-slate-50 text-slate-700',
    loading: 'border-sky-200 bg-sky-50 text-sky-700',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    error: 'border-red-200 bg-red-50 text-red-700',
    'missing-key': 'border-amber-200 bg-amber-50 text-amber-700',
  };

  const currentAddressStatusClass =
    addressStatusClasses[addressValidation.status] || addressStatusClasses.idle;

  if (redirectTarget) {
    return (
      <div className="min-h-screen bg-white px-4 py-8 text-slate-900 sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-[70vh] max-w-3xl items-center justify-center">
          <div className="w-full rounded-[2rem] border border-cyan-100 bg-white p-8 shadow-[0_25px_70px_-35px_rgba(8,145,178,0.35)] sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">
              G-CENTRA
            </p>
            <h1 className="mt-4 text-3xl font-black tracking-tight text-slate-950">
              Empresa creada
            </h1>
            <p className="mt-3 text-base leading-7 text-slate-600">
              {createdCompanyName
                ? `${createdCompanyName} ya quedó registrada.`
                : 'La empresa ya quedó registrada.'}{' '}
              Estamos preparando tu espacio de trabajo para ingresar sin volver al formulario.
            </p>

            <div className="mt-8 flex items-center gap-4 rounded-3xl border border-cyan-100 bg-cyan-50/80 px-5 py-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-cyan-200 bg-white">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-cyan-600 border-t-transparent" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Preparando tu espacio</p>
                <p className="mt-1 text-sm text-slate-600">
                  En segundos te llevamos al acceso de tu empresa.
                </p>
              </div>
            </div>

            {status.text ? (
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {status.text}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-8 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">
              G-CENTRA
            </p>
              <h1 className="mt-2 text-3xl font-black tracking-tight">Probar gratis 30 días</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              Creá tu empresa trial, validá la dirección de la base operativa y empezá a trabajar con el entorno real de G-CENTRA.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onBackHome}
              className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Volver al inicio
            </button>
            <button
              type="button"
              onClick={onGoToLogin}
              className="rounded-2xl bg-cyan-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-cyan-700"
            >
              Ingresar
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <aside className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-700">
                Qué incluye el trial
              </p>
              <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-600">
                <li>• logística y creación de rutas</li>
                <li>• fichas de transportistas y base operativa</li>
                <li>• hoja de ruta y operación inicial real</li>
                <li>• acceso al entorno empresa con configuración propia</li>
                <li>• acceso con contraseña o con Google, según prefieras</li>
              </ul>
            </div>

            <div className="rounded-3xl border border-cyan-100 bg-cyan-50/80 p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-700">
                Dirección con sugerencias
              </p>
              <p className="mt-3 text-sm leading-6 text-slate-700">
                Validamos la dirección operativa de tu empresa, depósito u oficina para tomarla como punto de referencia dentro de G-CENTRA.
              </p>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Escribí calle y número, elegí una opción de la lista y el sistema la guardará para mapas, rutas y destinos.
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-700">
                Privacidad
              </p>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Los datos cargados se utilizan para crear y configurar el entorno operativo de tu empresa dentro de G-CENTRA. La información es de uso reservado y se emplea exclusivamente para acceso, base operativa y funcionamiento interno del servicio.
              </p>
            </div>
          </aside>

          <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-6 rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">
                Elegí cómo querés acceder
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={activateGoogleMode}
                  disabled={connectingGoogle || submitting}
                  className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition ${
                    authMode === 'google'
                      ? 'border-cyan-300 bg-cyan-50 text-cyan-800'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"></path><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"></path><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"></path><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.443-2.57 4.473-4.902 5.713l6.19 5.238C39.712 35.619 44 29.561 44 24c0-1.341-.138-2.65-.389-3.917z"></path></svg>
                  <span>
                    {connectingGoogle
                      ? 'Conectando Google...'
                      : authMode === 'google'
                        ? 'Google vinculado'
                        : 'Usar Google para este alta'}
                  </span>
                </button>

                {authMode === 'google' ? (
                  <button
                    type="button"
                    onClick={switchToPasswordMode}
                    disabled={submitting}
                    className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Volver a contraseña
                  </button>
                ) : null}
              </div>

              <p className="mt-3 text-sm leading-6 text-slate-600">
                {authMode === 'google'
                  ? 'Google autentica tu identidad. Igual tenés que completar los datos de empresa y validar la dirección operativa.'
                  : 'También podés crear la empresa con email y contraseña si preferís un acceso clásico.'}
              </p>

              {authMode === 'google' && googleAccount?.email ? (
                <>
                  <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    Paso 1 listo: Google conectado con <span className="font-semibold">{googleAccount.email}</span>
                  </div>
                  <div className="mt-3 rounded-2xl border border-cyan-100 bg-white px-4 py-4">
                    <p className="text-sm font-bold text-slate-900">
                      Ahora seguí con estos pasos para terminar el alta
                    </p>
                    <ol className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
                      <li>
                        <span className="font-semibold text-slate-900">2.</span> Completá el nombre de la empresa y el responsable.
                      </li>
                      <li>
                        <span className="font-semibold text-slate-900">3.</span> Validá la dirección de la base operativa con una sugerencia.
                      </li>
                      <li>
                        <span className="font-semibold text-slate-900">4.</span> Aceptá privacidad y creá la empresa.
                      </li>
                    </ol>
                  </div>
                </>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm text-slate-700">
                Empresa *
                <input
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Responsable *
                <input
                  name="responsibleName"
                  value={form.responsibleName}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Email de acceso *
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="username"
                  disabled={authMode === 'google'}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Teléfono
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                />
              </label>

              <div className="sm:col-span-2" ref={autocompleteWrapRef}>
                <label className="block text-sm text-slate-700">
                  Dirección de la base operativa *
                  <input
                    name="addressLine"
                    value={form.addressLine}
                    onChange={(event) => {
                      handleChange(event);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => {
                      if (suggestions.length) setShowSuggestions(true);
                    }}
                    placeholder="Calle y número de oficina, depósito o base"
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  />
                </label>

                {showSuggestions && (loadingSuggestions || suggestions.length > 0) ? (
                  <div className="relative">
                    <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
                      {loadingSuggestions ? (
                        <div className="px-4 py-3 text-sm text-slate-500">
                          Buscando direcciones...
                        </div>
                      ) : null}

                      {!loadingSuggestions &&
                        suggestions.map((item, index) => (
                          <button
                            key={`${item.place_id || item.lat || 'addr'}-${index}`}
                            type="button"
                            onClick={() => applySuggestion(item)}
                            className="flex w-full flex-col items-start border-b border-slate-100 px-4 py-3 text-left transition hover:bg-slate-50 last:border-b-0"
                          >
                            <span className="text-sm font-semibold text-slate-800">
                              {item.address_line1 || item.formatted}
                            </span>
                            <span className="mt-1 text-xs text-slate-500">
                              {item.address_line2 || item.formatted}
                            </span>
                          </button>
                        ))}
                    </div>
                  </div>
                ) : null}
              </div>

              <label className="block text-sm text-slate-700">
                Localidad *
                <input
                  name="locality"
                  value={form.locality}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Provincia *
                <input
                  name="province"
                  value={form.province}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                />
              </label>

              <label className="block text-sm text-slate-700">
                País *
                <input
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                />
              </label>

              <label className="block text-sm text-slate-700">
                Código postal
                <input
                  name="postalCode"
                  value={form.postalCode}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                />
              </label>

              {authMode === 'password' ? (
                <>
                  <label className="block text-sm text-slate-700">
                    Contraseña *
                    <input
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                    />
                  </label>

                  <label className="block text-sm text-slate-700">
                    Confirmar contraseña *
                    <input
                      type="password"
                      name="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      autoComplete="new-password"
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                    />
                  </label>
                </>
              ) : (
                <div className="rounded-2xl border border-cyan-100 bg-cyan-50 px-4 py-3 text-sm leading-6 text-cyan-900 sm:col-span-2">
                  La contraseña no hace falta porque este alta va a quedar asociada a tu cuenta Google conectada.
                </div>
              )}
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <p className="font-semibold text-slate-800">Base operativa seleccionada</p>
              <p className="mt-1">
                {fullAddress || 'Escribí la dirección de tu oficina, depósito o base y elegí una sugerencia para validarla.'}
              </p>
            </div>

            <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm font-medium ${currentAddressStatusClass}`}>
              {addressValidation.text}
            </div>

            {addressValidation.status === 'success' && addressValidation.coords ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                Base lista: lat {addressValidation.coords.lat}, lng {addressValidation.coords.lng}
              </div>
            ) : null}

            <div className="mt-5 space-y-3">
              <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  name="acceptGeolocation"
                  checked={form.acceptGeolocation}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span>
                  Acepto que G-CENTRA guarde la dirección seleccionada como base operativa de la empresa para mapas, rutas y referencias internas. Esto no habilita rastreo personal.
                </span>
              </label>

              <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  name="acceptPrivacy"
                  checked={form.acceptPrivacy}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span>
                  Acepto el uso reservado de los datos para configuración, acceso y funcionamiento interno del servicio.
                </span>
              </label>
            </div>

            {status.text ? (
              <div
                className={`mt-5 rounded-2xl border px-4 py-3 text-sm font-medium ${
                  status.type === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}
              >
                {status.text}
              </div>
            ) : null}

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="rounded-2xl bg-cyan-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting
                  ? 'Creando espacio trial...'
                  : authMode === 'google'
                    ? 'Crear empresa trial con Google'
                    : 'Crear empresa trial'}
              </button>

              <button
                type="button"
                onClick={onGoToLogin}
                className="rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Ya tengo acceso
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrialSignupPage;
